import { Pressable, View, ActivityIndicator } from "react-native"
import MIcon from "@react-native-vector-icons/material-design-icons"
import * as Burnt from "burnt"
import { trpc } from "utils/trpc"
import { useState } from "react"
import { ar } from "i18n/ar"
import { Text, TextInput, ModalWrapper } from "./core"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { createSectionInput } from "@white-labels/input"
import { ImageUploadWrapper } from "./image-upload-wrapper"
import { useUploadImage } from "@src/hooks/use-product-image-upload"
import type { z } from "@white-labels/input"

type SectionFormProps = {
  onClose: () => void
  id?: number
  initialValues?: z.infer<typeof createSectionInput>
}

export function SectionForm({ onClose, id, initialValues }: SectionFormProps) {
  const utils = trpc.useUtils()
  const isEditMode = id != null
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(createSectionInput),
    defaultValues: {
      name: "",
      ...initialValues,
    },
  })

  const createSection = trpc.menu.createSection.useMutation()
  const updateSection = trpc.menu.updateSection.useMutation()
  const uploadImage = useUploadImage()
  const [imageUri, setImageUri] = useState<string | null>(null)

  const onSubmit = handleSubmit((data) => {
    if (!isEditMode && !imageUri) {
      Burnt.toast({ title: ar.pleaseSelectImage, preset: "error" })
      return
    }

    const onSuccess = (entityId: number) => {
      if (imageUri) {
        uploadImage.mutate(
          { entity: "section", entityId, uri: imageUri },
          {
            onSuccess: () => {
              utils.menu.get.refetch()
              onClose()
            },
          },
        )
      } else {
        utils.menu.get.refetch()
        onClose()
      }
    }

    if (isEditMode) {
      updateSection.mutate({ id, ...data }, { onSuccess: () => onSuccess(id) })
    } else {
      createSection.mutate(data, { onSuccess: (result) => onSuccess(result.id) })
    }
  })

  const isPending = createSection.isPending || updateSection.isPending || uploadImage.isPending

  return (
    <ModalWrapper onClose={onClose}>
      <View className="p-4 gap-4">
        <Text className="text-xl font-bold">{isEditMode ? ar.editSection : ar.newSection}</Text>

        <Controller control={control} name="name" render={({ field: { onChange, value } }) => <TextInput placeholder={ar.name} value={value} onChangeText={onChange} />} />

        <ImageUploadWrapper processing={uploadImage.isPending} onConfirm={(uri) => setImageUri(uri)}>
          {imageUri ? (
            <View className="w-full h-40 bg-gray-100 rounded items-center justify-center">
              <MIcon name="check-circle" size={48} color="#34A85A" />
              <Text className="text-gray-600 mt-2">{ar.imageSelected}</Text>
            </View>
          ) : (
            <View className="w-full h-40 bg-gray-100 rounded items-center justify-center border-2 border-dashed border-gray-300">
              <MIcon name="image-plus" size={48} color="#999" />
              <Text className="text-gray-500 mt-2">{isEditMode ? ar.tapToUploadImage : ar.tapToUploadImage}</Text>
            </View>
          )}
        </ImageUploadWrapper>

        <Pressable className="bg-blue-500 p-4 rounded items-center active:opacity-70" onPress={onSubmit} disabled={isPending}>
          {isPending ? <ActivityIndicator color="white" /> : <Text className="text-white font-bold">{isEditMode ? ar.save : ar.create}</Text>}
        </Pressable>
      </View>
    </ModalWrapper>
  )
}
